// Color Balance — Photoshop-style adjustment.
//
// Adjusts the color balance independently in Shadows, Midtones, and
// Highlights, with an optional Preserve Luminosity mode that rescales the
// output RGB to keep the original luminance (Rec.601).
//
// Alpha is never touched. Selection masking is delegated to
// `applyFilterInPlace`.

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface ColorBalanceToneParams {
  cyanRed: number;
  magentaGreen: number;
  yellowBlue: number;
}

export interface ColorBalanceParams {
  shadows: ColorBalanceToneParams;
  midtones: ColorBalanceToneParams;
  highlights: ColorBalanceToneParams;
  preserveLuminosity: boolean;
}

const DEFAULT_PARAMS: ColorBalanceParams = {
  shadows: { cyanRed: 0, magentaGreen: 0, yellowBlue: 0 },
  midtones: { cyanRed: 0, magentaGreen: 0, yellowBlue: 0 },
  highlights: { cyanRed: 0, magentaGreen: 0, yellowBlue: 0 },
  preserveLuminosity: true,
};

function clamp(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

function clampRange(v: number, lo: number, hi: number): number {
  if (!isFinite(v)) return 0;
  if (v < lo) return lo;
  if (v > hi) return hi;
  return v;
}

function luminance(r: number, g: number, b: number): number {
  return 0.3 * r + 0.59 * g + 0.11 * b;
}

function pickToneParams(
  lum: number,
  shadows: ColorBalanceToneParams,
  midtones: ColorBalanceToneParams,
  highlights: ColorBalanceToneParams,
): ColorBalanceToneParams {
  if (lum < 85) return shadows;
  if (lum > 170) return highlights;
  return midtones;
}

export const ColorBalance: Filter<ColorBalanceParams> = {
  id: "color-balance",
  displayName: "Color Balance",
  defaultParams: DEFAULT_PARAMS,

  apply(
    input: PixelBuffer,
    params: ColorBalanceParams,
    _selection: Selection | null,
  ): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;
    const n = w * h;

    const shadows = {
      cyanRed: clampRange(params.shadows.cyanRed, -100, 100),
      magentaGreen: clampRange(params.shadows.magentaGreen, -100, 100),
      yellowBlue: clampRange(params.shadows.yellowBlue, -100, 100),
    };
    const midtones = {
      cyanRed: clampRange(params.midtones.cyanRed, -100, 100),
      magentaGreen: clampRange(params.midtones.magentaGreen, -100, 100),
      yellowBlue: clampRange(params.midtones.yellowBlue, -100, 100),
    };
    const highlights = {
      cyanRed: clampRange(params.highlights.cyanRed, -100, 100),
      magentaGreen: clampRange(params.highlights.magentaGreen, -100, 100),
      yellowBlue: clampRange(params.highlights.yellowBlue, -100, 100),
    };
    const preserve = params.preserveLuminosity;

    for (let i = 0; i < n; i++) {
      const o = i * 4;
      const r = src[o] ?? 0;
      const g = src[o + 1] ?? 0;
      const b = src[o + 2] ?? 0;
      const a = src[o + 3] ?? 0;

      if (a === 0) {
        dst[o] = 0;
        dst[o + 1] = 0;
        dst[o + 2] = 0;
        dst[o + 3] = 0;
        continue;
      }

      const origLum = luminance(r, g, b);
      const tone = pickToneParams(origLum, shadows, midtones, highlights);

      let ro = r + (tone.cyanRed / 100) * 255;
      let go = g + (tone.magentaGreen / 100) * 255;
      let bo = b + (tone.yellowBlue / 100) * 255;

      ro = clamp(Math.round(ro));
      go = clamp(Math.round(go));
      bo = clamp(Math.round(bo));

      if (preserve) {
        const outLum = luminance(ro, go, bo);
        if (outLum > 0) {
          const scale = origLum / outLum;
          ro = clamp(Math.round(ro * scale));
          go = clamp(Math.round(go * scale));
          bo = clamp(Math.round(bo * scale));
        }
      }

      dst[o] = ro;
      dst[o + 1] = go;
      dst[o + 2] = bo;
      dst[o + 3] = a;
    }

    return out;
  },
};
